import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Copy, Check, Loader2, Mail, Monitor, Smartphone, Info } from 'lucide-react';
import { apiMethods } from '@/services/api';
import { getTheme } from '@/features/card/themes';
import { splitName, formatPhone, inkFor } from '@/features/card/lib/format';
import { rememberOwnCard } from '@/features/card/lib/device';

// Email clients only render simple, table-based HTML with inline styles:
// no CSS classes, no SVG, no web fonts, absolute https image URLs.
const esc = (v = '') =>
    String(v).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

const SHOP = 'https://nordic3d.us/hexlink.html?utm_source=hexlink&utm_medium=email_signature&utm_campaign=signature_footer';

// Neutral, readable colors for email (theme accents like neon green are hard to read on white).
const EXTRA_COLORS = ['#111827', '#1d4ed8', '#b91c1c', '#047857'];

function buildSignature({ user, names, color, opts, origin }) {
    const p = user.profile || {};
    const phone = (user.phoneNumbers || []).find((x) => x.isPrimary && x.isVisible !== false) || (user.phoneNumbers || []).find((x) => x.isVisible !== false);
    const email = (user.emailAddresses || []).find((x) => x.isPrimary && x.isVisible !== false)?.email || user.email;
    const cardUrl = `${origin}/card/${user.cardUuid}?utm_source=email`;
    const ver = (p.profilePicture || '').replace(/\D/g, '').slice(-10);
    const photo = p.profilePicture ? `${origin}/api/card/${user.cardUuid}/photo?w=192&fmt=jpg&v=${ver}` : null;
    const qr = `${origin}/api/card/${user.cardUuid}/qrcode?size=240`;
    const ink = inkFor(color);
    const role = [p.jobTitle, p.organization].filter(Boolean).map(esc).join(' &middot; ');
    const font = 'Arial,Helvetica,sans-serif';
    const link = `color:#374151;text-decoration:none;`;

    const contact = [
        opts.phone && phone ? `<a href="tel:${esc(phone.number.replace(/[^\d+]/g, ''))}" style="${link}">${esc(formatPhone(phone.number))}</a>` : '',
        email ? `<a href="mailto:${esc(email)}" style="${link}">${esc(email)}</a>` : '',
    ]
        .filter(Boolean)
        .join('<span style="color:#d1d5db;">&nbsp;&nbsp;|&nbsp;&nbsp;</span>');

    const html = `<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;font-family:${font};color:#1f2937;">
<tr>
${
    opts.photo && photo
        ? `<td valign="top" style="padding:0 16px 0 0;"><a href="${cardUrl}" style="text-decoration:none;"><img src="${photo}" width="84" height="84" alt="${esc(names.displayName)}" style="display:block;width:84px;height:84px;border-radius:42px;border:0;" /></a></td>`
        : ''
}
<td valign="top" style="padding:0 0 0 14px;border-left:3px solid ${color};">
<div style="font-family:${font};font-size:17px;line-height:22px;font-weight:bold;color:#111827;">${esc(names.displayName)}${
        names.nickname ? ` <span style="font-weight:normal;color:#6b7280;">&ldquo;${esc(names.nickname)}&rdquo;</span>` : ''
    }</div>
${role ? `<div style="font-family:${font};font-size:13px;line-height:19px;color:#4b5563;">${role}</div>` : ''}
${contact ? `<div style="font-family:${font};font-size:13px;line-height:19px;padding-top:6px;">${contact}</div>` : ''}
<table cellpadding="0" cellspacing="0" border="0" style="border-collapse:collapse;margin-top:10px;"><tr>
<td bgcolor="${color}" style="background:${color};border-radius:6px;"><a href="${cardUrl}" style="display:inline-block;padding:8px 16px;font-family:${font};font-size:13px;line-height:16px;font-weight:bold;color:${ink};text-decoration:none;border-radius:6px;">Save my contact &rarr;</a></td>
</tr></table>
${
    opts.promo
        ? `<div style="font-family:${font};font-size:11px;line-height:16px;color:#9ca3af;padding-top:8px;">My digital business card &middot; <a href="${SHOP}" style="color:#9ca3af;text-decoration:underline;">Get your own HexLink</a></div>`
        : ''
}
</td>
${
    opts.qr
        ? `<td valign="top" style="padding:0 0 0 16px;"><a href="${cardUrl}" style="text-decoration:none;"><img src="${qr}" width="84" height="84" alt="Scan to save my contact" style="display:block;width:84px;height:84px;border:0;" /></a></td>`
        : ''
}
</tr>
</table>`;

    const text = [
        names.displayName,
        [p.jobTitle, p.organization].filter(Boolean).join(' · '),
        opts.phone && phone ? formatPhone(phone.number) : '',
        email || '',
        `Save my contact: ${cardUrl}`,
    ]
        .filter(Boolean)
        .join('\n');

    return { html, text, cardUrl };
}

const GUIDES = [
    {
        id: 'gmail',
        label: 'Gmail',
        icon: Monitor,
        copy: 'rich',
        steps: [
            'Open Gmail on a computer.',
            'Click the gear icon (top right), then "See all settings".',
            'On the General tab, scroll to "Signature" and click "Create new". Give it a name.',
            'Click in the signature box and paste (Ctrl+V, or Cmd+V on a Mac).',
            'Under "Signature defaults", pick it for new emails and replies.',
            'Scroll to the bottom and click "Save Changes".',
        ],
    },
    {
        id: 'outlook',
        label: 'Outlook',
        icon: Monitor,
        copy: 'rich',
        steps: [
            'New Outlook or Outlook.com: click the gear icon, then Account → Signatures.',
            'Click "New signature", give it a name, and paste into the box.',
            'Choose it as the default for new messages and replies, then click Save.',
            'Classic Outlook for Windows: File → Options → Mail → "Signatures…" → New, paste, then OK.',
        ],
    },
    {
        id: 'apple',
        label: 'Apple Mail (Mac)',
        icon: Monitor,
        copy: 'rich',
        steps: [
            'Open Mail, then Mail → Settings (or Preferences) → Signatures.',
            'Pick your account on the left and click the + button.',
            'Paste into the right-hand box.',
            'Uncheck "Always match my default message font" so the design keeps its look.',
            'Choose the new signature from the "Choose Signature" menu.',
        ],
    },
    {
        id: 'iphone',
        label: 'iPhone / iPad',
        icon: Smartphone,
        copy: 'text',
        steps: [
            'Open this page on your iPhone and tap "Copy plain text" below.',
            'Open Settings → Apps → Mail → Signature (on older iOS: Settings → Mail → Signature).',
            'Clear the old signature, then tap and hold → Paste.',
            'Tip: you can also try "Copy signature". If the formatting looks off, shake your iPhone and tap "Undo Change Attributes" to keep the styled version.',
        ],
    },
    {
        id: 'android',
        label: 'Android (Gmail app)',
        icon: Smartphone,
        copy: 'text',
        steps: [
            'Tap "Copy plain text" below.',
            'Open the Gmail app, tap the menu (☰) → Settings → your email address.',
            'Tap "Mobile Signature", paste, and tap OK.',
            'The phone app only supports plain text. Your styled signature from Gmail on a computer still shows on emails sent from a computer.',
        ],
    },
];

export default function EmailSignature() {
    const { data, isLoading } = useQuery({
        queryKey: ['userProfile'],
        queryFn: async () => (await apiMethods.users.getProfile()).data,
    });
    const user = data?.user;
    const theme = getTheme(user?.cardTheme);
    const themeColors = useMemo(() => [...new Set([user?.cardAccentColor, ...theme.accents].filter(Boolean))], [user?.cardAccentColor, theme.accents]);
    const [color, setColor] = useState(null);
    const [opts, setOpts] = useState({ photo: true, phone: true, qr: false, promo: true });
    const [copied, setCopied] = useState('');
    const [guide, setGuide] = useState('gmail');
    const previewRef = useRef(null);

    useEffect(() => {
        rememberOwnCard(user?.cardUuid);
    }, [user?.cardUuid]);

    if (isLoading || !user) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
            </div>
        );
    }

    const activeColor = color || themeColors[0] || '#1d4ed8';
    const names = splitName(user.profile);
    const sig = buildSignature({ user, names, color: activeColor, opts, origin: window.location.origin });

    const flash = (k) => {
        setCopied(k);
        setTimeout(() => setCopied(''), 2000);
    };

    // Copy as rich text so pasting into Gmail/Outlook/Apple Mail keeps the design.
    const copyRich = async () => {
        try {
            if (window.ClipboardItem && navigator.clipboard?.write) {
                await navigator.clipboard.write([
                    new window.ClipboardItem({
                        'text/html': new Blob([sig.html], { type: 'text/html' }),
                        'text/plain': new Blob([sig.text], { type: 'text/plain' }),
                    }),
                ]);
                flash('rich');
                return;
            }
        } catch {
            // fall back to selecting the preview
        }
        const range = document.createRange();
        range.selectNode(previewRef.current);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
        document.execCommand('copy');
        sel.removeAllRanges();
        flash('rich');
    };
    const copyText = async (value, key) => {
        await navigator.clipboard.writeText(value);
        flash(key);
    };

    const toggles = [
        ['photo', 'Photo'],
        ['phone', 'Phone number'],
        ['qr', 'QR code'],
        ['promo', '"Get your own HexLink" line'],
    ];
    const activeGuide = GUIDES.find((g) => g.id === guide);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-bold text-gray-900">Email Signature</h1>
                <p className="mt-1 text-sm text-gray-500">
                    Put your HexLink card in every email you send. People can save your contact in one tap, right from your signature.
                </p>
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-5 [&>*]:min-w-0">
                {/* Preview + copy */}
                <div className="lg:col-span-3 space-y-4">
                    <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <div className="flex items-center gap-2 border-b border-gray-100 bg-gray-50 px-4 py-2.5 text-xs text-gray-500">
                            <Mail className="h-4 w-4" />
                            How it looks in an email
                        </div>
                        {/* Stays white in the dark dashboard: this is how it looks in someone's inbox */}
                        <div className="n3d-light px-5 py-6 overflow-x-auto">
                            <p className="mb-4 text-sm text-gray-600">
                                Thanks again for your time today.
                                <br />
                                <br />—
                            </p>
                            {/* eslint-disable-next-line react/no-danger -- generated from escaped values above */}
                            <div ref={previewRef} dangerouslySetInnerHTML={{ __html: sig.html }} />
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <button type="button" onClick={copyRich} className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-700">
                            {copied === 'rich' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                            {copied === 'rich' ? 'Copied! Now paste it' : 'Copy signature'}
                        </button>
                        <button type="button" onClick={() => copyText(sig.text, 'text')} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                            {copied === 'text' ? <Check className="h-4 w-4 text-green-600" /> : <Smartphone className="h-4 w-4" />}
                            {copied === 'text' ? 'Copied' : 'Copy plain text (phones)'}
                        </button>
                        <button type="button" onClick={() => copyText(sig.html, 'code')} className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                            {copied === 'code' ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                            {copied === 'code' ? 'Copied' : 'Copy HTML code'}
                        </button>
                    </div>
                    <p className="flex items-start gap-2 text-xs text-gray-500">
                        <Info className="mt-0.5 h-4 w-4 flex-none" />
                        Your signature updates itself: the photo and buttons load from your HexLink, so if you change your photo, emails you send later show the new one.
                    </p>
                </div>

                {/* Options */}
                <div className="lg:col-span-2 rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-5">
                    <div>
                        <h2 className="text-sm font-semibold text-gray-900">Button color</h2>
                        <p className="text-xs text-gray-500">Matches your {theme.label} card theme. Darker colors read best in email.</p>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {[...themeColors, ...EXTRA_COLORS.filter((c) => !themeColors.includes(c))].map((c) => (
                                <button
                                    key={c}
                                    type="button"
                                    onClick={() => setColor(c)}
                                    aria-label={`Use color ${c}`}
                                    aria-pressed={activeColor === c}
                                    className={`h-9 w-9 rounded-full border-2 transition ${activeColor === c ? 'border-gray-900 scale-110' : 'border-white shadow ring-1 ring-gray-200'}`}
                                    style={{ background: c }}
                                />
                            ))}
                        </div>
                    </div>
                    <div>
                        <h2 className="text-sm font-semibold text-gray-900">Show</h2>
                        <div className="mt-2 space-y-2">
                            {toggles.map(([key, label]) => (
                                <label key={key} className="flex items-center gap-3 text-sm text-gray-700">
                                    <input
                                        type="checkbox"
                                        checked={opts[key]}
                                        onChange={(e) => setOpts((o) => ({ ...o, [key]: e.target.checked }))}
                                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                    />
                                    {label}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div className="rounded-lg bg-gray-50 p-3">
                        <div className="text-xs font-medium text-gray-500">Your card link (for anywhere else)</div>
                        <div className="mt-1 flex items-center gap-2">
                            <code className="flex-1 truncate text-xs text-gray-800">{sig.cardUrl}</code>
                            <button type="button" onClick={() => copyText(sig.cardUrl, 'link')} className="text-xs font-semibold text-primary-600">
                                {copied === 'link' ? 'Copied' : 'Copy'}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* How-to */}
            <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                <div className="border-b border-gray-100 px-5 pt-4">
                    <h2 className="text-base font-semibold text-gray-900">Add it to your email</h2>
                    <div className="-mb-px mt-3 flex gap-1 overflow-x-auto" role="tablist">
                        {GUIDES.map((g) => (
                            <button
                                key={g.id}
                                type="button"
                                role="tab"
                                aria-selected={guide === g.id}
                                onClick={() => setGuide(g.id)}
                                className={`inline-flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2 text-sm font-medium ${
                                    guide === g.id ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <g.icon className="h-4 w-4" />
                                {g.label}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="p-5" role="tabpanel">
                    <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
                        <span className="text-gray-600">First:</span>
                        {activeGuide.copy === 'rich' ? (
                            <button type="button" onClick={copyRich} className="inline-flex items-center gap-1.5 rounded-md bg-primary-50 px-3 py-1.5 font-semibold text-primary-700 hover:bg-primary-100">
                                {copied === 'rich' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                {copied === 'rich' ? 'Copied' : 'Copy signature'}
                            </button>
                        ) : (
                            <button type="button" onClick={() => copyText(sig.text, 'text')} className="inline-flex items-center gap-1.5 rounded-md bg-primary-50 px-3 py-1.5 font-semibold text-primary-700 hover:bg-primary-100">
                                {copied === 'text' ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                {copied === 'text' ? 'Copied' : 'Copy plain text'}
                            </button>
                        )}
                    </div>
                    <ol className="space-y-3">
                        {activeGuide.steps.map((s, i) => (
                            <li key={s} className="flex gap-3 text-sm text-gray-700">
                                <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-gray-900 text-xs font-bold text-white">{i + 1}</span>
                                <span className="pt-0.5">{s}</span>
                            </li>
                        ))}
                    </ol>
                    <p className="mt-5 text-xs text-gray-500">Menus move around now and then. If a step doesn&apos;t match, search the app&apos;s settings for &quot;signature&quot;.</p>
                </div>
            </div>
        </div>
    );
}
